import { C as getScriptTagJson, B as formatShopifyPrice, W as fetchConfig, ad as trackAddToCart, H as publish, P as PUB_SUB_EVENTS } from './app-chunk-common-BPZAsnhF.js';

/**
 * Stock status for a variantStock status for a variantStock status for a variantStock status for a variant
 * @typedef {"coming-soon"|"available"|"oos"} StockStatus
 */

const sizeExpr =
  /(\(10 tablet tube\)|\(8 tube box\)|\(canister\)|\(12 sachets\)|\(1[26] sachets\)|8-ct|14-ct|16-ct)$/i;

const productScriptSelector = ".is-product-script-data";

const addToCartText = "Add to Cart";
const addToBoxText = "Add to Box";

const availabilityText = {
  inStock: "In Stock",
  outOfStock: "Out of Stock",
  comingSoon: "Coming Soon",
};

/**
 * @param {number} sellingPlanId
 * @param {import("../types/products").Product} product
 * @returns {number}
 */
const getFrequencyFromSellingPlanID = (sellingPlanId, product) => {
  if (product.selling_plan_groups.length > 0) {
    const group = product.selling_plan_groups[0];
    const plan = group.selling_plans.find((x) => x.id === sellingPlanId);
    if (plan) {
      return Number((plan.name.match(/\d+/) || [])[0] || 1);
    }
  }

  console.error("We expect a selling plan!");
  return 1;
};

/**
 * @param {string} srcA
 * @param {string} srcB
 * @returns {boolean}
 */
const areSourcesEqual = (srcA, srcB) => {
  const a = srcA.split("https://").pop();
  const b = srcB.split("https://").pop();
  return a === b;
};
/**
 * Replaces all images with [data-variant-img] tag. It will update src and srcset when needed.
 *
 * If src or srcset will be the same value it skips it to avoid triggering a loading event.
 *
 * @param {HTMLElement} container
 * @param {import("../types/products").ProductVariant} variant
 */
const updateFeaturedImage = (container, variant) => {
  /** @type {NodeListOf<HTMLImageElement>} */
  const images = container.querySelectorAll("[data-variant-img]");
  images.forEach((imageEl) => {
    if (!imageEl || !variant.featured_image) {
      return;
    }

    imageEl.classList.add("is-loading-image");

    imageEl.onload = () => {
      imageEl.classList.remove("is-loading-image");
    };

    if (imageEl.dataset.variantImg === "srcset") {
      const existing = imageEl.getAttribute("srcset") || "";
      const sources = existing.split(", ").map((x) => x.trim());
      const newPath = variant.featured_image.src.split("?")[0];
      const newSources = sources.map((src) => {
        const [url, size] = src.split(" ");
        const [_, query] = url.split("?");
        return `${newPath}?${query} ${size}`;
      });

      const value = newSources.join(", ");
      if (existing !== value) {
        imageEl.setAttribute("srcset", newSources.join(", "));
      }
    }

    const imgSrc = getSizedImageSrc(variant.featured_image.src);

    // Won't always have prorocol in check.
    if (!areSourcesEqual(imageEl.src, imgSrc)) {
      imageEl.alt = variant.title;
      imageEl.src = imgSrc;
    } else {
      //The loading event won't trigger if src is the same so manually remove class.
      imageEl.classList.remove("is-loading-image");
    }
  });
};

/**
 * @param {HTMLElement} container
 * @param {import("../types/products").ProductVariant} variant
 */
const updateVisibleContent = (container, variant) => {
  /** @type {NodeListOf<HTMLElement>} */
  const elements = container.querySelectorAll("[data-only-for-variant]");
  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];
    const variantId = parseInt(el.dataset.onlyForVariant || "");
    el.classList.toggle("is-visible", variant.id == variantId);
  }
};

/**
 * @param {string} imageSrc
 * @param {string} [size]
 */
const getSizedImageSrc = (imageSrc, size = "900x900") => {
  const parts = imageSrc.split(".png");
  return parts[0] + "_" + size + ".png" + parts[1];
};

/**
 * @param {HTMLElement} container
 * @returns {import("../types/products").Product}
 */
const getProductData = (container) => {
  const productOrNull = getScriptTagJson(productScriptSelector, container);

  if (!productOrNull) {
    throw new Error("Product data should exist");
  }

  // @ts-ignore
  return productOrNull;
};

/**
 * @param {HTMLElement} container
 * @param {string} variantName
 * @returns {import("../types/products").ProductVariant}
 */
const getVariantInfo = (container, variantName) => {
  const [_product, variant] = getVariantInfoWithProduct(container, variantName);
  return variant;
};

/**
 * @param {HTMLElement} container
 * @param {string} variantName
 * @returns {[import("../types/products").Product, import("../types/products").ProductVariant]}
 */
const getVariantInfoWithProduct = (container, variantName) => {
  const product = getProductData(container);
  const variant = product.variants.find((x) => x.option1 === variantName);
  if (!variant) {
    throw new Error(
      `Could not find variant with name "${variantName}" in script block`
    );
  }
  return [product, variant];
};

/**
 * @param {HTMLElement} container
 * @param {import("../types/products").ProductVariant} variant
 */
const updateAvailabilityText = (container, variant) => {
  const stockStatus = getStockStatus(container, variant.title);

  container.querySelectorAll("[data-variant-available-text]").forEach((el) => {
    if (el instanceof HTMLElement) {
      if (stockStatus === "coming-soon") {
        el.setAttribute("data-stock-status", "coming-soon");
        el.innerText = availabilityText.comingSoon;
      } else if (stockStatus === "available") {
        el.setAttribute("data-stock-status", "available");
        el.innerText = availabilityText.inStock;
      } else {
        el.setAttribute("data-stock-status", "oos");
        el.innerText = availabilityText.outOfStock;
      }
    }
  });
};

/**
 * @param {HTMLElement} container
 * @param {number} variantId
 */
const isComingSoon = (container, variantId) => {
  let comingSoonIds = (container.getAttribute("coming-soon-ids") || "")
    .split(",")
    .filter(Boolean)
    .map(Number);

  return comingSoonIds.includes(variantId);
};

/**
 * @param {HTMLElement} container
 * @param {string} variantTitle
 * @returns {"coming-soon"|"available"|"oos"}
 */
const getStockStatus = (container, variantTitle) => {
  const [product, variant] = getVariantInfoWithProduct(container, variantTitle);
  if (product.tags.includes("force-oos")) {
    return "oos";
  }

  if (isComingSoon(container, variant.id)) {
    return "coming-soon";
  }

  if (variant.available) {
    return "available";
  }
  return "oos";
};

/**
 * @param {HTMLElement} container
 * @param {string} variantName
 * @param {boolean} updateImages
 */
const updateContentWithVariant = (
  container,
  variantName,
  updateImages
) => {
  const variant = getVariantInfo(container, variantName);
  if (updateImages) {
    updateFeaturedImage(container, variant);
  }

  updatePrice(container, variant);
  updateAvailabilityText(container, variant);
  updateCheckoutBtnText(container, variant);
  updateVariantLinks(container, variant);
  updateVisibleContent(container, variant);
};

/**
 * @param {string|null} prefix
 * @param {number} rawPrice
 * @param {HTMLElement|document} [container]
 */
const updatePriceAttributesWithPrefix = (
  prefix,
  rawPrice,
  container = document
) => {
  const price = formatShopifyPrice(rawPrice);

  container.querySelectorAll("[data-variant-price]").forEach((priceEl) => {
    if (priceEl instanceof HTMLElement) {
      const text = price;
      priceEl.innerText = text;
    }
  });
};

/**
 * @param {number} rawPrice
 * @param {HTMLElement|document} [container]
 */
const updatePriceAttributes = (rawPrice, container = document) => {
  updatePriceAttributesWithPrefix(null, rawPrice, container);
};

/**
 * @param {HTMLElement} container
 * @param {import("../types/products").ProductVariant} variant
 */
const updatePrice = (container, variant) => {
  updatePriceAttributes(variant.price, container);
};

/**
 * @param {HTMLElement} container
 * @param {import("../types/products").ProductVariant} variant
 */
const updateVariantLinks = (container, variant) => {
  const productData = getProductData(container);
  const url = `/products/${productData.handle}?variant=${variant.id}`;

  container.querySelectorAll("[data-variant-url]").forEach((el) => {
    if (el instanceof HTMLAnchorElement) {
      el.href = url;
    }
  });
};

/**
 * @param {HTMLElement} container
 * @param {import("../types/products").ProductVariant} variant
 * @param {boolean} [isOnetime]
 * @param {boolean} [isNewPDP]
 */
const updateCheckoutBtnText = (
  container,
  variant,
  isOnetime = true,
  isNewPDP = false
) => {
  let text = addToCartText;

  const buttonSelector = ".product-atc-button__wrapper .quick-add-btn";
  const button = container.querySelector(buttonSelector);

  if (!button) {
    return;
  }

  // Dont update button text if not dynamic
  if (button.classList.contains("is-dynamic") === false) {
    return;
  }

  if (isOnetime === false && variant.selling_plan_allocations.length > 0) {
    const salePrice = variant.selling_plan_allocations[0].price;
    const f = formatShopifyPrice;
    text =
      `${addToBoxText} ` +
      `<span class="is-compare-price">${f(variant.price)}</span>` +
      `<span class="is-separator"> | </span>` +
      `<span class="is-sale-price">${f(salePrice)}</span>`;
  } else {
    text = [
      `<span>${addToCartText}</span>`,
      `<span class="pdp-main__price-sep">-</span>`,
      `<span>${formatShopifyPrice(variant.price)}</span>`,
    ].join("");
  }

  const textEl = button.querySelector(".button__text");
  if (textEl) {
    textEl.innerHTML = text;
  } else {
    button.innerHTML = text;
  }
};

/**
 * @param {string} source
 * @param {number} quantity
 * @param {string|number} variantId
 * @param {number|null|undefined} sellingPlanId
 * @param {Response} res
 * @param {HTMLElement|null} trackingElement
 * @param {HTMLElement|null} triggerElement
 */
function triggerAddToCartUpdate(
  source,
  quantity,
  variantId,
  sellingPlanId,
  res,
  trackingElement,
  triggerElement
) {
  try {
    if (trackingElement) {
      const isRecurring = !!sellingPlanId;
      const product = getProductData(trackingElement);
      const category = trackingElement.getAttribute("data-category") || null;
      const source = trackingElement.getAttribute("data-source") || null;
      let frequency = "";

      if (isRecurring) {
        frequency = String(
          getFrequencyFromSellingPlanID(sellingPlanId, product)
        );
      }

      trackAddToCart(
        quantity,
        Number(variantId),
        product,
        category,
        source,
        isRecurring,
        frequency
      );
    }
  } catch (e) {
    console.error("[ERROR] Tracking product added event", e);
  }

  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: source,
    productVariantId: variantId,
    cartData: res,
    triggerElement: triggerElement,
  });
}

/**
 * @param {string|number} variantId
 * @param {number} quantity
 * @param {number|null} sellingPlanId
 * @param {HTMLElement|null} trackingElement
 * @param {HTMLElement|null} triggerElement
 */
const addToCartAndOpenCart = async (
  variantId,
  quantity,
  sellingPlanId,
  trackingElement,
  triggerElement
) => {
  /** @type {import("./cart-drawer").CartDrawer} */
  // @ts-ignore
  const cart = document.querySelector("cart-drawer");

  let rawBody = {
    quantity,
    id: variantId,
    sections: cart.getSectionsToRender().map((section) => section.section),
    sections_url: window.location.pathname,
    selling_plan: sellingPlanId ? sellingPlanId : undefined,
  };

  const body = JSON.stringify(rawBody);

  cart.enableLoading();

  return fetch(`${window.routes.cart_add_url}`, {
    ...fetchConfig(),
    ...{ body },
  })
    .then((res) => res.json())
    .then((res) => {
      triggerAddToCartUpdate(
        "product-form",
        quantity,
        variantId,
        sellingPlanId,
        res,
        trackingElement,
        triggerElement
      );
    });
};

/**
 * @param {number} ms
 * @returns {Promise<void>}
 */
async function delay(ms = 3000) {
  return new Promise((res) => {
    window.setTimeout(() => res(), ms);
  });
}

function openRewardHistory() {
  /** @type {import('./cart-drawer').CartDrawer} */
  // @ts-ignore
  const drawer = document.querySelector("cart-drawer");
  drawer.openPointsHistory();
}

async function openCartDrawer() {
  /** @type {import('./cart-drawer').CartDrawer} */
  // @ts-ignore
  const drawer = document.querySelector("cart-drawer");
  drawer.open();
}

async function ensureSwellLoaded() {
  return new Promise(async (res) => {
    while (true) {
      if (window.__LOYALTY_LOADED__ === true) {
        // @ts-ignore
        res();
        return;
      }
      await delay(300);
    }
  });
}

/**
 * @param {string} productTitle
 * @param {string} variantTitle
 * @returns {string|null}
 */
const getSizeFromVariantTitle = (productTitle, variantTitle) => {
  const matches = variantTitle.match(sizeExpr);

  if (variantTitle.indexOf("% off") !== -1) {
    return null;
  }

  let variantSize = null;

  if (matches !== null && matches.length > 0) {
    const s = matches[0].toLowerCase();
    if (s === "(canister)") {
      variantSize = "canister";
    }
    if (s === "(10 tube box)") {
      variantSize = "tube10";
    }
    if (s === "(8 tube box)") {
      variantSize = "tubebox8";
    }
    if (s === "(12 sachets)") {
      variantSize = "sachets12";
    }
    if (s === "(16 sachets)") {
      variantSize = "sachets16";
    }
    if (s === "8-ct") {
      variantSize = "box8";
    }
    if (s === "14-ct") {
      variantSize = "box14";
    }
    if (s === "16-ct") {
      variantSize = "box16";
    }
  }

  if (!variantSize) {
    const title = productTitle.replace(" 15% Off", "");
    if (
      title === "Nuun Sport" ||
      title == "Nuun Rest" ||
      title == "Nuun Energy" ||
      title == "Nuun Immunity" ||
      title == "Nuun Immunity Seasonal Flavors"
    ) {
      variantSize = "tube10";
    }

    if (title === "Nuun Vitamins") {
      variantSize = "tube12";
    }
  }

  return variantSize;
};

export { getStockStatus as a, updateAvailabilityText as b, addToCartAndOpenCart as c, getSizeFromVariantTitle as d, ensureSwellLoaded as e, getVariantInfo as f, getProductData as g, getVariantInfoWithProduct as h, openCartDrawer as i, openRewardHistory as o, updateContentWithVariant as u };
